import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Button, ErrorText, Field, Input, Modal, Select, Spinner } from '../../components/ui'
import { clp, formatRut, formatTime, insuranceLabel, weekdayName, SERVICE_CATEGORY_LABELS } from '../../utils/format'
import type { InsuranceProvider, Patient, Professional, Service, Slot, Location } from '../../types/api'
import { Icon } from '../../components/ui/icons'

/**
 * Flujo de nueva cita (patrón Healtheaton mejorado):
 * 1. RUT identifica al paciente (o formulario breve si es nuevo)
 * 2. Prestación + profesional
 * 3. Tira semanal de horarios disponibles
 * 4. Confirmación
 */
export function NewAppointmentModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState(1)
  const [error, setError] = useState('')

  // Paso 1: paciente
  const [rut, setRut] = useState('')
  const [patient, setPatient] = useState<Patient | null>(null)
  const [rutChecked, setRutChecked] = useState(false)
  const [newPatient, setNewPatient] = useState({ firstName: '', lastName: '', phone: '', email: '', insuranceProviderId: '' })

  // Paso 2: prestación y profesional
  const [serviceId, setServiceId] = useState('')
  const [professionalId, setProfessionalId] = useState('')

  // Paso 3: slot
  const [slot, setSlot] = useState<Slot | null>(null)
  const [weekOffset, setWeekOffset] = useState(0)

  // Paso 4: logística según la modalidad de la prestación (H7)
  const [logistics, setLogistics] = useState({ visitAddress: '', visitNotes: '', videoCallUrl: '' })

  const services = useQuery({
    queryKey: ['services'],
    queryFn: async () => (await api.get<Service[]>('/catalog/services')).data,
  })
  const professionals = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => (await api.get<Professional[]>('/professionals')).data,
  })
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => (await api.get<Location[]>('/catalog/locations')).data,
  })
  // Previsión desde el catálogo (DT-60): las mismas opciones que ve el
  // paciente en /agendar, mantenidas por la clínica.
  const insuranceProviders = useQuery({
    queryKey: ['insurance-providers'],
    queryFn: async () => (await api.get<InsuranceProvider[]>('/catalog/insurance-providers')).data,
  })

  const from = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + weekOffset * 7)
    return d
  }, [weekOffset])

  const slots = useQuery({
    queryKey: ['availability', professionalId, from.toDateString()],
    queryFn: async () =>
      (
        await api.get<Slot[]>('/appointments/availability', {
          params: { professionalId, from: from.toISOString(), days: 7 },
        })
      ).data,
    enabled: step === 3 && Boolean(professionalId),
  })

  async function checkRut() {
    setError('')
    try {
      const { data } = await api.get(`/patients/by-rut/${encodeURIComponent(rut)}`)
      setRutChecked(true)
      if (data.found) {
        setPatient(data.patient)
        setStep(2)
      }
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  const createPatient = useMutation({
    mutationFn: async () =>
      (
        await api.post<Patient>('/patients', {
          rut,
          ...newPatient,
          consentAccepted: true,
        })
      ).data,
    onSuccess: (created) => {
      setPatient(created)
      setStep(2)
      setError('')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const createAppointment = useMutation({
    mutationFn: async () => {
      if (!patient || !slot) throw new Error('faltan datos')
      return (
        await api.post('/appointments', {
          patientId: patient.id,
          professionalId,
          serviceId,
          locationId: slot.locationId,
          boxId: slot.boxId,
          startsAt: slot.startsAt,
          // Solo se envía lo que corresponde a la modalidad de la prestación.
          ...(service?.modality === 'HOME_VISIT'
            ? { visitAddress: logistics.visitAddress || null, visitNotes: logistics.visitNotes || null }
            : {}),
          ...(service?.modality === 'TELEMEDICINE' ? { videoCallUrl: logistics.videoCallUrl || null } : {}),
        })
      ).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      onClose()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const service = services.data?.find((s) => s.id === serviceId)
  const professional = professionals.data?.find((p) => p.id === professionalId)
  const locationName = (id: string) => locations.data?.find((l) => l.id === id)?.name ?? ''

  // Agrupar slots por día para la tira semanal.
  const slotsByDay = useMemo(() => {
    const grouped = new Map<string, Slot[]>()
    for (const s of slots.data ?? []) {
      if (!s.available) continue
      const key = new Date(s.startsAt).toDateString()
      grouped.set(key, [...(grouped.get(key) ?? []), s])
    }
    return grouped
  }, [slots.data])

  return (
    <Modal title={`Nueva cita — paso ${step} de 4`} onClose={onClose} wide>
      <div className="space-y-4">
        {step === 1 && (
          <>
            <Field label="RUT del paciente" hint="El RUT identifica al paciente; si ya existe, sus datos se cargan solos.">
              <div className="flex gap-2">
                <Input value={rut} onChange={(e) => { setRut(e.target.value); setRutChecked(false) }} placeholder="12.345.678-5" />
                <Button onClick={checkRut} disabled={!rut}>Buscar</Button>
              </div>
            </Field>
            {rutChecked && !patient && (
              <div className="space-y-3 rounded-lg border border-brand-200 bg-soft p-3">
                <p className="text-sm font-medium text-ink">Paciente nuevo — completa sus datos básicos:</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Nombres">
                    <Input value={newPatient.firstName} onChange={(e) => setNewPatient({ ...newPatient, firstName: e.target.value })} />
                  </Field>
                  <Field label="Apellidos">
                    <Input value={newPatient.lastName} onChange={(e) => setNewPatient({ ...newPatient, lastName: e.target.value })} />
                  </Field>
                  <Field label="Teléfono (WhatsApp)">
                    <Input value={newPatient.phone} onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })} placeholder="+56 9 …" />
                  </Field>
                  <Field label="Email">
                    <Input type="email" value={newPatient.email} onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })} />
                  </Field>
                  <Field label="Previsión">
                    <Select
                      value={newPatient.insuranceProviderId}
                      onChange={(e) => setNewPatient({ ...newPatient, insuranceProviderId: e.target.value })}
                    >
                      <option value="">Selecciona…</option>
                      {(insuranceProviders.data ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
                <p className="text-xs text-gray-500">Al crear la ficha se registra el consentimiento de tratamiento de datos (Ley 21.719).</p>
                <Button
                  variant="cta"
                  disabled={!newPatient.firstName || !newPatient.lastName || !newPatient.insuranceProviderId || createPatient.isPending}
                  onClick={() => createPatient.mutate()}
                >
                  Crear paciente y continuar
                </Button>
              </div>
            )}
          </>
        )}

        {step === 2 && patient && (
          <>
            <p className="rounded-lg bg-soft px-3 py-2 text-sm">
              Paciente: <strong>{patient.firstName} {patient.lastName}</strong> · {formatRut(patient.rut)} · {insuranceLabel(patient)}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Prestación">
                <Select value={serviceId} onChange={(e) => { setServiceId(e.target.value); setSlot(null) }}>
                  <option value="">Selecciona…</option>
                  {(services.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      [{SERVICE_CATEGORY_LABELS[s.category]}] {s.name} — {clp(s.basePrice)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Profesional">
                <Select value={professionalId} onChange={(e) => { setProfessionalId(e.target.value); setSlot(null) }}>
                  <option value="">Selecciona…</option>
                  {(professionals.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstName} {p.lastName} — {p.specialty ?? ''}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {service?.requiresDeposit && (
              <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <Icon name="warning" className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Esta prestación requiere abono
                {service.depositPercent ? ` del ${service.depositPercent}%` : ''}
                {service.depositAmount ? ` de ${clp(service.depositAmount)}` : ''} para confirmar.</span>
              </p>
            )}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}>← Volver</Button>
              <Button disabled={!serviceId || !professionalId} onClick={() => setStep(3)}>Ver disponibilidad →</Button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="flex items-center justify-between">
              <Button variant="secondary" onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))} disabled={weekOffset === 0}>
                ← Semana anterior
              </Button>
              <p className="text-sm font-medium">Semana del {from.toLocaleDateString('es-CL')}</p>
              <Button variant="secondary" onClick={() => setWeekOffset(weekOffset + 1)}>Semana siguiente →</Button>
            </div>
            {slots.isLoading ? (
              <Spinner />
            ) : slotsByDay.size === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">
                Sin horarios disponibles esta semana. Prueba la siguiente o cambia de profesional.
              </p>
            ) : (
              <div className="grid max-h-80 gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
                {Array.from(slotsByDay.entries()).map(([day, daySlots]) => (
                  <div key={day} className="rounded-lg border border-gray-200 p-2">
                    <p className="mb-1.5 text-xs font-semibold uppercase text-steel-600">{weekdayName(daySlots[0].startsAt)}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {daySlots.map((s) => (
                        <button
                          key={s.startsAt}
                          onClick={() => setSlot(s)}
                          className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                            slot?.startsAt === s.startsAt ? 'bg-brand-600 text-white' : 'bg-soft text-brand-700 hover:bg-brand-200'
                          }`}
                        >
                          {formatTime(s.startsAt)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(2)}>← Volver</Button>
              <Button disabled={!slot} onClick={() => setStep(4)}>Continuar →</Button>
            </div>
          </>
        )}

        {step === 4 && patient && slot && service && professional && (
          <>
            <div className="space-y-1.5 rounded-lg bg-soft p-4 text-sm">
              <p className="font-display font-semibold text-ink">Resumen de la cita</p>
              <p className="flex items-center gap-1.5"><Icon name="person" className="h-4 w-4 text-brand-600" />{patient.firstName} {patient.lastName} · {formatRut(patient.rut)}</p>
              <p className="flex items-center gap-1.5"><Icon name="sportsMedicine" className="h-4 w-4 text-brand-600" />{service.name} ({service.durationMin} min) — {clp(service.basePrice)}</p>
              <p className="flex items-center gap-1.5"><Icon name="vitals" className="h-4 w-4 text-brand-600" />{professional.firstName} {professional.lastName}</p>
              <p className="flex items-center gap-1.5"><Icon name="agenda" className="h-4 w-4 text-brand-600" />{weekdayName(slot.startsAt)} · {formatTime(slot.startsAt)} h</p>
              <p className="flex items-center gap-1.5"><Icon name="location" className="h-4 w-4 text-brand-600" />{locationName(slot.locationId)}</p>
            </div>

            {service.modality === 'HOME_VISIT' && (
              <div className="space-y-2 rounded-lg border border-brand-100 bg-soft p-3">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                  <Icon name="route" className="h-4 w-4 text-brand-600" />
                  Visita a domicilio
                </p>
                <Field label="Dirección de la visita" hint={patient.address ? `Dirección del paciente: ${patient.address}` : 'El paciente no tiene dirección registrada'}>
                  <Input
                    value={logistics.visitAddress}
                    placeholder={patient.address ?? 'Calle, número, comuna'}
                    onChange={(e) => setLogistics({ ...logistics, visitAddress: e.target.value })}
                  />
                </Field>
                <Field label="Referencias (opcional)">
                  <Input
                    value={logistics.visitNotes}
                    placeholder="Timbre, piso, estacionamiento…"
                    onChange={(e) => setLogistics({ ...logistics, visitNotes: e.target.value })}
                  />
                </Field>
              </div>
            )}

            {service.modality === 'TELEMEDICINE' && (
              <div className="space-y-2 rounded-lg border border-brand-100 bg-soft p-3">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                  <Icon name="video" className="h-4 w-4 text-brand-600" />
                  Telemedicina
                </p>
                <Field label="Enlace de la videollamada" hint="Se puede agregar o cambiar después; el paciente lo ve en su portal.">
                  <Input
                    value={logistics.videoCallUrl}
                    placeholder="https://…"
                    onChange={(e) => setLogistics({ ...logistics, videoCallUrl: e.target.value })}
                  />
                </Field>
              </div>
            )}
            <p className="text-xs text-gray-500">
              Se enviará confirmación por WhatsApp/email y un recordatorio 24 h antes.
            </p>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(3)}>← Volver</Button>
              <Button variant="cta" disabled={createAppointment.isPending} onClick={() => createAppointment.mutate()}>
                {createAppointment.isPending ? 'Agendando…' : 'Confirmar y agendar'}
              </Button>
            </div>
          </>
        )}

        <ErrorText>{error}</ErrorText>
      </div>
    </Modal>
  )
}
